import { useEffect, useRef } from "react";
import { env } from "@/shared/constants/env";
import { useSessionStore } from "@/shared/stores/sessionStore";
import { setAgentAvailability } from "../services/agentService";
import { useAgentStore } from "../stores/agentStore";

/**
 * Maintient la Main réellement disponible — contrat du 2026-08-28, §4.
 *
 * ## Le piège que ce hook désamorce
 * Se déclarer disponible ne suffit pas, et c'est la cause la plus fréquente
 * d'une Main qui ne reçoit rien alors que tout paraît normal.
 *
 * Le serveur bascule une Main `AVAILABLE` en `OFFLINE` après **quinze minutes**
 * sans position. Or — et c'est là que tout se joue — **`POST /location` ne
 * touche jamais au statut** : après un tunnel, un écran verrouillé ou un onglet
 * mis en veille plus de quinze minutes, la Main se croit disponible, son GPS
 * remonte parfaitement, et elle ne recevra plus jamais d'offre.
 *
 * Le seul remède est de RE-POSTER le statut. Le contrat demande de le faire à
 * chaque retour au premier plan et après toute reprise réseau : c'est
 * exactement ce que fait ce hook.
 *
 * ## Ce qu'il ne fait pas
 * Il ne met JAMAIS une Main en ligne d'autorité. Il ne réaffirme que ce que
 * l'app affiche déjà : si l'écran dit « en service », le serveur doit le savoir
 * aussi. Une Main hors ligne le reste.
 */

/**
 * Intervalle minimal entre deux réaffirmations.
 *
 * Le plafond de la passerelle est de 240 requêtes par minute et par IP, TOUTES
 * ROUTES CONFONDUES — position comprise, qui part déjà toutes les quelques
 * secondes. Un `visibilitychange` peut se déclencher en rafale (changement
 * d'onglet répété) : sans ce frein, on grillerait le budget commun.
 */
const REPOST_THROTTLE_MS = 30_000;

export function useAgentPresence(): void {
  const status = useAgentStore((s) => s.status);
  const agentId = useSessionStore((s) => s.agentId);

  // Lus via des refs : les écouteurs sont posés UNE fois, et doivent voir la
  // valeur courante sans être réabonnés à chaque changement de statut.
  const statusRef = useRef(status);
  statusRef.current = status;
  const agentIdRef = useRef(agentId);
  agentIdRef.current = agentId;

  const lastRepostRef = useRef(0);

  useEffect(() => {
    const repost = (raison: string) => {
      // On ne réaffirme QUE ce que l'app affiche déjà.
      if (statusRef.current !== "AVAILABLE") return;
      const id = agentIdRef.current;
      if (!id) return;

      const now = Date.now();
      if (now - lastRepostRef.current < REPOST_THROTTLE_MS) return;
      lastRepostRef.current = now;

      if (env.isDev) console.warn(`[agent] réaffirmation AVAILABLE (${raison})`);
      // Best effort : un échec ne doit rien casser à l'écran. La prochaine
      // occasion — retour d'onglet, reprise réseau — retentera.
      void setAgentAvailability(id, "AVAILABLE").catch(() => undefined);
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") repost("retour au premier plan");
    };
    const onOnline = () => repost("reprise réseau");

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);

    // Au montage aussi : l'app a pu être rouverte après une longue veille, et
    // c'est précisément le cas où le serveur a déjà éteint la Main.
    repost("montage");

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
    };
  }, []);
}
